/**
 * PROD1.7.8 N3 — barème visuel pour le score de confiance d'une extraction.
 *
 * 4 niveaux hardcodés (décision design 2026-05-28 : pas de configuration
 * par Prompt tant qu'on n'a pas de données empiriques justifiant des
 * seuils par domaine).
 *
 * Seuils sur le score brut [0..1] :
 *   ≥ 0.95          → "excellent"  (vert emerald)
 *   0.85 .. 0.949   → "good"       (jaune amber)
 *   0.70 .. 0.849   → "attention"  (orange)
 *   < 0.70          → "weak"       (rouge)
 *   null/undefined  → "unknown"    (gris — extraction legacy sans Prompt 1.3)
 *
 * Pour les filtres tabulaires, on agrège en 5 buckets (les 4 niveaux + unknown).
 */

export type ConfidenceLevel = 'excellent' | 'good' | 'attention' | 'weak' | 'unknown';

export interface ConfidenceLevelMeta {
  level: ConfidenceLevel;
  label: string;
  /** Label avec la plage de pourcentage — pour les options de filtre
   *  (ex. « Excellente (95-100%) »). Distinct de `label` (tooltips, aria). */
  rangeLabel: string;
  /** Tailwind classes pour badge (bg + text + border). */
  badgeClassName: string;
  /** Tailwind classes pour cellule de tableau (bg + text). */
  cellClassName: string;
  tooltip: string;
}

export const CONFIDENCE_LEVELS: Record<ConfidenceLevel, ConfidenceLevelMeta> = {
  excellent: {
    level: 'excellent',
    label: 'Excellente',
    rangeLabel: 'Excellente (95-100%)',
    badgeClassName: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    cellClassName: 'bg-emerald-50 text-emerald-800',
    tooltip: 'Confiance ≥ 95% — extraction quasi parfaite.',
  },
  good: {
    level: 'good',
    label: 'Bonne',
    rangeLabel: 'Bonne (85-94%)',
    badgeClassName: 'bg-amber-100 text-amber-800 border-amber-200',
    cellClassName: 'bg-amber-50 text-amber-800',
    tooltip: 'Confiance 85–94% — quelques champs medium, normal.',
  },
  attention: {
    level: 'attention',
    label: 'Attention',
    rangeLabel: 'Attention (70-84%)',
    badgeClassName: 'bg-orange-100 text-orange-800 border-orange-200',
    cellClassName: 'bg-orange-100 text-orange-900',
    tooltip: 'Confiance 70–84% — plusieurs champs incertains, vigilance.',
  },
  weak: {
    level: 'weak',
    label: 'Faible',
    rangeLabel: 'Faible (0-69%)',
    badgeClassName: 'bg-red-100 text-red-800 border-red-200',
    cellClassName: 'bg-red-100 text-red-900',
    tooltip: 'Confiance < 70% — révision recommandée.',
  },
  unknown: {
    level: 'unknown',
    label: 'Inconnue',
    rangeLabel: 'Inconnue',
    badgeClassName: 'bg-gray-100 text-gray-600 border-gray-300',
    cellClassName: 'bg-gray-50 text-gray-500',
    tooltip: 'Aucun score — extraction antérieure au Prompt 1.3 (format scalaire legacy).',
  },
};

/** Map un score brut [0..1] (ou null/undefined) vers son niveau visuel. */
export function getConfidenceLevel(score: number | null | undefined): ConfidenceLevel {
  if (score === null || score === undefined || Number.isNaN(score)) return 'unknown';
  const n = typeof score === 'number' ? score : parseFloat(String(score));
  if (Number.isNaN(n)) return 'unknown';
  if (n >= 0.95) return 'excellent';
  if (n >= 0.85) return 'good';
  if (n >= 0.7) return 'attention';
  return 'weak';
}

export function getConfidenceMeta(score: number | null | undefined): ConfidenceLevelMeta {
  return CONFIDENCE_LEVELS[getConfidenceLevel(score)];
}
